// ============================================================================
// TRADING PRESET MODES + SETTING ACTIONS (v3.9.7)
// ============================================================================
// Shared definitions for built-in modes and one-click setting actions.
// Each mode is a FULL snapshot of ScheduleNodeParams.
// Each action is either a param_patch or an operational action.
// ============================================================================

// ---------------------------------------------------------------------------
// BUILT-IN MODES
// ---------------------------------------------------------------------------

export const BUILT_IN_MODES = [
  // =========================================================================
  // FAST GREEN SCALP — "HTF-Safe Entries + Instant Profit Capture"
  // Core principle: Only trade WITH the HTF trend, require room-to-target,
  // exit fast (TP1 80/20 split), time-to-green + hard timebox, ATR-based SL.
  // No rescue. No counter-trend. No neutral-HTF trades (by default).
  // =========================================================================
  {
    id: 'fast_green_scalp_5x',
    name: 'Fast Green Scalp — 5×',
    shortName: 'FGS 5×',
    description: 'HTF-aligned scalps. TP 2.0% net, SL 0.80%. Timebox 30min. Limit-first exits. No rescue, no counter-trend.',
    icon: '🎯',
    intent: 'fast_green_scalp',
    tags: ['scalp', '5x', 'fast', 'htf-safe', 'trend-only', 'leverage'],
    isBuiltIn: true,
    params: {
      scanInterval: '1m',
      manageInterval: '1m',
      confidenceThreshold: '84',
      confirmationMode: false,
      strictEntryEnabled: true,
      enableVolumeControl: true,
      volumeStrictness: 'high',
      enableCorrelation: true,
      sellFilterStrict: true,
      tradingMode: 'futures',
      leverage: 5,
      leverageEntryMode: 'moderate',
      maxConcurrentPositions: 5,
      allocationMode: 'equal_weight',
      reservePercent: 15,
      maxPerTrade: 3,
      maxPerSymbol: 5,
      positionSizePercent: '3',
      positionSizeUsd: '100',
      stopLossPercent: '0.80',
      takeProfitPercent: '2.0',
      exitModePreset: 'custom',
      useTrailingStop: 'Yes',
      trailingStopPercent: '0.20',
      trailingStopAiMode: true,
      useTrailingBenefit: 'Yes',
      trailingBenefitActivation: 0.3,
      trailingBenefitLockPercent: 70,
      trailingBenefitAiMode: true,
      cooldownMinutes: '2',
      cooldownPerSymbol: 'Yes',
      rescueScaleInEnabled: false,
      maxRescueAdds: '0',
      rescueLevels: '0',
      rescueReservePercent: 0,
      maxSignalsPerRun: '5',
      // FGS-specific settings
      fastGreenScalpEnabled: true,
      fgsTrendOnlyPolicy: true,
      fgsAllowRangeScalpsInNeutralHTF: true,
      fastGreenTimeoutSec: 120,
      scalpTimeboxMinutes: 30,
      partialTpPercent: 70,
      fgsLossCapBaseSLGross: 0.80,
      fgsLossCapKSL: 1.1,
      fgsHardMaxLossNet: -1.50,
      executionMode: 'limit_first',
      exitExecutionMode: 'limit_first',
      htfTrendFilterEnabled: true,
      fgsSymbolQualityFilterEnabled: true,
      fgsSymbolBlacklistHours: 6,
      // Scalp Gate (ON)
      scalpGateEnabled: true,
      scalpGateProfile: 'balanced',
      scalpGateApplyOn5x: true,
      scalpGateApplyOn10x: true,
      scalpGateApplyOn20x: true,
      disable30mTrendFilter: true,
      disable5mConfirmationGate: true,
      disableConsensusGate: true,
      disableExhaustionGate: true,
      disableImpulseGate: true,
      disableEntryTimingNear15mHigh: false,
      priceDriftGateMode: 'loose',
      chopPolicy: 'block_all',
      widenEntryTimingPercent: 0.5,
      // Smart Exit (ON)
      intentAutoDetectEnabled: true,
      adaptiveProfitTrailEnabled: true,
      adaptiveProfitTrailATRMultiplier: 0.5,
      trailingStopFloorEnabled: true,
      multiTargetTPEnabled: true,
      zeroLockMeaningfulEnabled: true,
      zeroLockMeaningfulRule: 'sustained',
      zeroLockSustainedTicks: 3,
      aiCloseGuardEnabled: true,
      aiCloseGuardMinTPFraction: 0.15,
      // Win Rate Boost Pack (ON)
      microImpulseGateEnabled: true,
      fastToGreenTimeoutEnabled: true,
      mode1AntiChopStrictEnabled: true,
      applyWinRateBoostOn5x: true,
      applyWinRateBoostOn10x: true,
      applyWinRateBoostOn20x: true,
    },
  },
  // =========================================================================
  // SCALP MODES — "Many Small Wins"
  // Core principle: Fast confirmation, tight invalidation, no holding through
  // structural reversals. Leverage determines how tight.
  // =========================================================================
  {
    id: 'many_small_wins_scalp_5x',
    name: 'Many Small Wins (Scalp) — 5×',
    shortName: 'Scalp 5×',
    description: 'Short holds, fast profit capture, strict discipline. SL 1.0%, TP 2.5%. R:R = 2.5:1. HTF trend filter. No rescue.',
    icon: '⚡',
    intent: 'many_small_wins',
    tags: ['scalp', '5x', 'fast', 'leverage'],
    isBuiltIn: true,
    params: {
      scanInterval: '5m',
      manageInterval: '1m',
      confidenceThreshold: '84',
      confirmationMode: false,
      strictEntryEnabled: true,
      enableVolumeControl: true,
      volumeStrictness: 'high',
      enableCorrelation: true,
      sellFilterStrict: true,
      tradingMode: 'futures',
      leverage: 5,
      leverageEntryMode: 'moderate',
      maxConcurrentPositions: 5,
      allocationMode: 'equal_weight',
      reservePercent: 15,
      maxPerTrade: 3,
      maxPerSymbol: 5,
      positionSizePercent: '3',
      positionSizeUsd: '100',
      stopLossPercent: '1.0',
      takeProfitPercent: '2.5',
      exitModePreset: 'custom',
      useTrailingStop: 'Yes',
      trailingStopPercent: '0.30',
      trailingStopAiMode: true,
      profitTrailDistanceNet: 0.30,
      cooldownMinutes: '2',
      cooldownPerSymbol: 'Yes',
      executionMode: 'limit_first',
      exitExecutionMode: 'limit_first',
      htfTrendFilterEnabled: true,
      // Rescue DISABLED for scalp — prevents SL deferral
      rescueScaleInEnabled: false,
      maxRescueAdds: '0',
      rescueLevels: '0',
      rescueReservePercent: 0,
      rescueTriggerThresholdPct: -3.0,
      rescuePremiumPct: 10,
      rescueCooldownSec: 45,
      hardLossWallPct: -3.0,
      tslBehavior: 'DISABLE_TSL_UNTIL_RESCUES_SPENT',
      maxSignalsPerRun: '5',
      // Win Rate Boost Pack
      microImpulseGateEnabled: true,
      fastToGreenTimeoutEnabled: true,
      mode1AntiChopStrictEnabled: true,
      applyWinRateBoostOn5x: true,
      applyWinRateBoostOn10x: true,
      applyWinRateBoostOn20x: true,
      // Scalp Gate
      scalpGateEnabled: true,
      scalpGateProfile: 'balanced',
      scalpGateApplyOn5x: true,
      scalpGateApplyOn10x: true,
      scalpGateApplyOn20x: true,
      disable30mTrendFilter: true,
      disable5mConfirmationGate: true,
      disableConsensusGate: true,
      disableExhaustionGate: true,
      disableImpulseGate: true,
      disableEntryTimingNear15mHigh: false,
      priceDriftGateMode: 'loose',
      chopPolicy: 'allow_limited',
      widenEntryTimingPercent: 0.5,
      // Smart Exit Enhancements
      intentAutoDetectEnabled: true,
      adaptiveProfitTrailEnabled: true,
      adaptiveProfitTrailATRMultiplier: 0.4,
      trailingStopFloorEnabled: true,
      multiTargetTPEnabled: true,
      multiTargetTightenAfterR1: 0.7,
      // Zero-Lock
      zeroLockMeaningfulEnabled: true,
      zeroLockMeaningfulRule: 'sustained',
      zeroLockSustainedTicks: 2,
      zeroLockTrailingEnabled: true,
      beFloorBufferPercent: 0.05,
      slippageBufferPercent: 0.10,
      aiCloseGuardEnabled: true,
      aiCloseGuardMinTPFraction: 0.15,
    },
  },
  {
    id: 'many_small_wins_scalp_10x',
    name: 'Many Small Wins (Scalp) — 10×',
    shortName: 'Scalp 10×',
    description: 'Ultra-tight scalping for 10×. SL 0.8%, TP 2.0%. R:R = 2.5:1. HTF trend filter. No rescue.',
    icon: '🔥',
    intent: 'many_small_wins',
    tags: ['scalp', '10x', 'fast', 'leverage', 'strict'],
    isBuiltIn: true,
    params: {
      scanInterval: '5m',
      manageInterval: '1m',
      confidenceThreshold: '88',
      confirmationMode: false,
      strictEntryEnabled: true,
      enableVolumeControl: true,
      volumeStrictness: 'high',
      enableCorrelation: true,
      sellFilterStrict: true,
      tradingMode: 'futures',
      leverage: 10,
      leverageEntryMode: 'strict',
      maxConcurrentPositions: 3,
      allocationMode: 'equal_weight',
      reservePercent: 20,
      maxPerTrade: 2,
      maxPerSymbol: 3,
      positionSizePercent: '2',
      positionSizeUsd: '100',
      stopLossPercent: '0.8',
      takeProfitPercent: '2.0',
      exitModePreset: 'custom',
      useTrailingStop: 'Yes',
      trailingStopPercent: '0.20',
      trailingStopAiMode: true,
      profitTrailDistanceNet: 0.20,
      cooldownMinutes: '2',
      cooldownPerSymbol: 'Yes',
      executionMode: 'limit_first',
      exitExecutionMode: 'limit_first',
      htfTrendFilterEnabled: true,
      // Rescue DISABLED for scalp — prevents SL deferral
      rescueScaleInEnabled: false,
      maxRescueAdds: '0',
      rescueLevels: '0',
      rescueReservePercent: 0,
      rescueTriggerThresholdPct: -2.5,
      rescuePremiumPct: 10,
      rescueCooldownSec: 30,
      hardLossWallPct: -3.0,
      tslBehavior: 'DISABLE_TSL_UNTIL_RESCUES_SPENT',
      maxSignalsPerRun: '4',
      // Win Rate Boost Pack
      microImpulseGateEnabled: true,
      fastToGreenTimeoutEnabled: true,
      mode1AntiChopStrictEnabled: true,
      applyWinRateBoostOn5x: true,
      applyWinRateBoostOn10x: true,
      applyWinRateBoostOn20x: true,
      // Scalp Gate
      scalpGateEnabled: true,
      scalpGateProfile: 'balanced',
      scalpGateApplyOn5x: true,
      scalpGateApplyOn10x: true,
      scalpGateApplyOn20x: true,
      disable30mTrendFilter: true,
      disable5mConfirmationGate: true,
      disableConsensusGate: true,
      disableExhaustionGate: true,
      disableImpulseGate: true,
      disableEntryTimingNear15mHigh: false,
      priceDriftGateMode: 'loose',
      chopPolicy: 'allow_limited',
      widenEntryTimingPercent: 0.5,
      // Smart Exit Enhancements
      intentAutoDetectEnabled: true,
      adaptiveProfitTrailEnabled: true,
      adaptiveProfitTrailATRMultiplier: 0.3,
      trailingStopFloorEnabled: true,
      multiTargetTPEnabled: true,
      multiTargetTightenAfterR1: 0.7,
      // Zero-Lock
      zeroLockMeaningfulEnabled: true,
      zeroLockMeaningfulRule: 'sustained',
      zeroLockSustainedTicks: 2,
      zeroLockTrailingEnabled: true,
      beFloorBufferPercent: 0.03,
      slippageBufferPercent: 0.08,
      aiCloseGuardEnabled: true,
      aiCloseGuardMinTPFraction: 0.15,
    },
  },
  {
    id: 'many_small_wins_scalp_20x',
    name: 'Many Small Wins (Scalp) — 20×',
    shortName: 'Scalp 20×',
    description: 'Extreme discipline. SL 0.6%, TP 1.5%. R:R = 2.5:1. Buy-only, no rescue. HTF trend filter.',
    icon: '💎',
    intent: 'many_small_wins',
    tags: ['scalp', '20x', 'ultra', 'leverage', 'strict'],
    isBuiltIn: true,
    params: {
      scanInterval: '5m',
      manageInterval: '1m',
      confidenceThreshold: '92',
      confirmationMode: false,
      strictEntryEnabled: true,
      enableVolumeControl: true,
      volumeStrictness: 'high',
      enableCorrelation: true,
      sidePolicy: 'buy_only',
      sellFilterStrict: true,
      tradingMode: 'futures',
      leverage: 20,
      leverageEntryMode: 'strict',
      maxConcurrentPositions: 2,
      allocationMode: 'equal_weight',
      reservePercent: 25,
      maxPerTrade: 1.5,
      maxPerSymbol: 2,
      positionSizePercent: '1.5',
      positionSizeUsd: '80',
      stopLossPercent: '0.6',
      takeProfitPercent: '1.5',
      exitModePreset: 'custom',
      useTrailingStop: 'Yes',
      trailingStopPercent: '0.12',
      trailingStopAiMode: true,
      profitTrailDistanceNet: 0.15,
      cooldownMinutes: '2',
      cooldownPerSymbol: 'Yes',
      // Rescue DISABLED at 20× — too risky
      rescueScaleInEnabled: false,
      maxRescueAdds: '0',
      rescueLevels: '0',
      rescueReservePercent: 0,
      maxSignalsPerRun: '3',
      // Win Rate Boost Pack
      microImpulseGateEnabled: true,
      fastToGreenTimeoutEnabled: true,
      mode1AntiChopStrictEnabled: true,
      applyWinRateBoostOn5x: true,
      applyWinRateBoostOn10x: true,
      applyWinRateBoostOn20x: true,
      // Scalp Gate
      scalpGateEnabled: true,
      scalpGateProfile: 'conservative',
      scalpGateApplyOn5x: true,
      scalpGateApplyOn10x: true,
      scalpGateApplyOn20x: true,
      disable30mTrendFilter: true,
      disable5mConfirmationGate: true,
      disableConsensusGate: true,
      disableExhaustionGate: true,
      disableImpulseGate: true,
      disableEntryTimingNear15mHigh: false,
      priceDriftGateMode: 'loose',
      chopPolicy: 'allow_limited',
      widenEntryTimingPercent: 0.5,
      // Smart Exit Enhancements
      intentAutoDetectEnabled: true,
      adaptiveProfitTrailEnabled: true,
      adaptiveProfitTrailATRMultiplier: 0.25,
      trailingStopFloorEnabled: true,
      multiTargetTPEnabled: true,
      multiTargetTightenAfterR1: 0.65,
      // Zero-Lock (aggressive at 20×)
      zeroLockMeaningfulEnabled: true,
      zeroLockMeaningfulRule: 'sustained',
      zeroLockSustainedTicks: 2,
      zeroLockTrailingEnabled: true,
      beFloorBufferPercent: 0.02,
      slippageBufferPercent: 0.06,
      aiCloseGuardEnabled: true,
      aiCloseGuardMinTPFraction: 0.15,
    },
  },
  // =========================================================================
  // HYBRID MODES — "Fewer Larger Wins"
  // Core principle: Let winners run, require strong HTF confirmation,
  // wider trailing to capture big moves. Rescue available for recovery.
  // =========================================================================
  {
    id: 'fewer_larger_wins_hybrid_5x',
    name: 'Hybrid (Fewer Larger Wins) — 5×',
    shortName: 'Hybrid 5×',
    description: 'Fewer entries, let winners run. SL 1.4%, TP 3.0%. R:R = 2.1:1. Rescue 3 levels. HTF trend filter.',
    icon: '📈',
    intent: 'fewer_larger_wins',
    tags: ['hybrid', 'swing', '5x', 'runner', 'leverage'],
    isBuiltIn: true,
    params: {
      scanInterval: '15m',
      manageInterval: '1m',
      confidenceThreshold: '82',
      confirmationMode: false,
      strictEntryEnabled: true,
      enableVolumeControl: true,
      volumeStrictness: 'medium',
      enableCorrelation: true,
      sellFilterStrict: true,
      tradingMode: 'futures',
      leverage: 5,
      leverageEntryMode: 'moderate',
      maxConcurrentPositions: 3,
      allocationMode: 'equal_weight',
      reservePercent: 15,
      maxPerTrade: 3,
      maxPerSymbol: 5,
      positionSizePercent: '3',
      positionSizeUsd: '150',
      stopLossPercent: '1.4',
      takeProfitPercent: '3.0',
      exitModePreset: 'custom',
      useTrailingStop: 'Yes',
      trailingStopPercent: '0.80',
      trailingStopAiMode: true,
      profitTrailDistanceNet: 0.50,
      cooldownMinutes: '4',
      cooldownPerSymbol: 'Yes',
      // Rescue (3 levels for recovery)
      rescueScaleInEnabled: true,
      maxRescueAdds: '3',
      rescueLevels: '3',
      rescueReservePercent: 10,
      rescueTriggerThresholdPct: -4.0,
      rescuePremiumPct: 10,
      rescueCooldownSec: 60,
      hardLossWallPct: -8.0,
      tslBehavior: 'DISABLE_TSL_UNTIL_RESCUES_SPENT',
      maxSignalsPerRun: '3',
      executionMode: 'limit_first',
      exitExecutionMode: 'limit_first',
      htfTrendFilterEnabled: true,
      // Smart Exit
      intentAutoDetectEnabled: true,
      adaptiveProfitTrailEnabled: true,
      adaptiveProfitTrailATRMultiplier: 0.5,
      trailingStopFloorEnabled: true,
      multiTargetTPEnabled: true,
      multiTargetTightenAfterR1: 0.7,
      // Zero-Lock (patient)
      zeroLockMeaningfulEnabled: true,
      zeroLockMeaningfulRule: 'sustained',
      zeroLockSustainedTicks: 3,
      zeroLockTrailingEnabled: true,
      beFloorBufferPercent: 0.08,
      slippageBufferPercent: 0.10,
      aiCloseGuardEnabled: true,
      aiCloseGuardMinTPFraction: 0.15,
      scalpGateEnabled: false,
    },
  },
  {
    id: 'fewer_larger_wins_trend_10x',
    name: 'Hybrid (Fewer Larger Wins) — 10×',
    shortName: 'Hybrid 10×',
    description: 'Trend-following at 10×. SL 1.0%, TP 2.5%. R:R = 2.5:1. Rescue 2 levels. HTF trend filter.',
    icon: '🚀',
    intent: 'fewer_larger_wins',
    tags: ['hybrid', 'trend', '10x', 'runner', 'leverage', 'strict'],
    isBuiltIn: true,
    params: {
      scanInterval: '15m',
      manageInterval: '1m',
      confidenceThreshold: '86',
      confirmationMode: false,
      strictEntryEnabled: true,
      enableVolumeControl: true,
      volumeStrictness: 'high',
      enableCorrelation: true,
      sellFilterStrict: true,
      tradingMode: 'futures',
      leverage: 10,
      leverageEntryMode: 'strict',
      maxConcurrentPositions: 2,
      allocationMode: 'equal_weight',
      reservePercent: 20,
      maxPerTrade: 2,
      maxPerSymbol: 3,
      positionSizePercent: '2',
      positionSizeUsd: '100',
      stopLossPercent: '1.0',
      takeProfitPercent: '2.5',
      exitModePreset: 'custom',
      useTrailingStop: 'Yes',
      trailingStopPercent: '0.50',
      trailingStopAiMode: true,
      profitTrailDistanceNet: 0.35,
      cooldownMinutes: '4',
      cooldownPerSymbol: 'Yes',
      // Rescue (2 levels at 10×)
      rescueScaleInEnabled: true,
      maxRescueAdds: '2',
      rescueLevels: '2',
      rescueReservePercent: 5,
      rescueTriggerThresholdPct: -3.0,
      rescuePremiumPct: 10,
      rescueCooldownSec: 45,
      hardLossWallPct: -8.0,
      tslBehavior: 'DISABLE_TSL_UNTIL_RESCUES_SPENT',
      maxSignalsPerRun: '3',
      // Smart Exit
      intentAutoDetectEnabled: true,
      adaptiveProfitTrailEnabled: true,
      adaptiveProfitTrailATRMultiplier: 0.4,
      trailingStopFloorEnabled: true,
      multiTargetTPEnabled: true,
      multiTargetTightenAfterR1: 0.7,
      // Zero-Lock
      zeroLockMeaningfulEnabled: true,
      zeroLockMeaningfulRule: 'sustained',
      zeroLockSustainedTicks: 3,
      zeroLockTrailingEnabled: true,
      beFloorBufferPercent: 0.05,
      slippageBufferPercent: 0.08,
      aiCloseGuardEnabled: true,
      aiCloseGuardMinTPFraction: 0.15,
      scalpGateEnabled: false,
    },
  },
  // =========================================================================
  // PRO TRADER BRAIN MODES — "Selective / Thesis-Based"
  // Core principle: Fewer trades, higher-quality entries, thesis memory across
  // scans. AI trailing always on. Stricter gates, prefer pending candidates
  // over immediate entry when signal is not clean.
  // =========================================================================
  {
    id: 'pro_trader_brain_5x',
    name: 'Pro Trader Brain — 5×',
    shortName: 'Pro 5×',
    description: 'Fewer trades, higher-quality entries, thesis memory. Selective mode with AI trailing.',
    icon: '🧠',
    intent: 'pro_selective',
    tags: ['pro', 'selective', 'thesis', '5x', 'leverage'],
    isBuiltIn: true,
    params: {
      scanInterval: '5m',
      manageInterval: '1m',
      confidenceThreshold: '82',
      confirmationMode: false,
      strictEntryEnabled: true,
      enableVolumeControl: true,
      volumeStrictness: 'high',
      enableCorrelation: true,
      sellFilterStrict: true,
      tradingMode: 'futures',
      leverage: 5,
      leverageEntryMode: 'strict',
      maxConcurrentPositions: 3,
      allocationMode: 'equal_weight',
      reservePercent: 15,
      maxPerTrade: 3,
      maxPerSymbol: 3,
      positionSizePercent: '3',
      positionSizeUsd: '100',
      stopLossPercent: '1.0',
      takeProfitPercent: '2.5',
      exitModePreset: 'custom',
      useTrailingStop: 'Yes',
      trailingStopPercent: '0.50',
      trailingStopAiMode: true,
      profitTrailDistanceNet: 0.50,
      cooldownMinutes: '10',
      cooldownPerSymbol: 'Yes',
      executionMode: 'limit_first',
      exitExecutionMode: 'limit_first',
      htfTrendFilterEnabled: true,
      // Rescue (2 levels — valuable thesis trades)
      rescueScaleInEnabled: true,
      maxRescueAdds: '2',
      rescueLevels: '2',
      rescueReservePercent: 10,
      rescueTriggerThresholdPct: -4.0,
      rescuePremiumPct: 10,
      rescueCooldownSec: 60,
      hardLossWallPct: -6.0,
      tslBehavior: 'DISABLE_TSL_UNTIL_RESCUES_SPENT',
      maxSignalsPerRun: '3',
      // Smart Exit
      intentAutoDetectEnabled: true,
      adaptiveProfitTrailEnabled: true,
      adaptiveProfitTrailATRMultiplier: 0.5,
      trailingStopFloorEnabled: true,
      multiTargetTPEnabled: true,
      multiTargetTightenAfterR1: 0.7,
      // Zero-Lock (patient)
      zeroLockMeaningfulEnabled: true,
      zeroLockMeaningfulRule: 'sustained',
      zeroLockSustainedTicks: 3,
      zeroLockTrailingEnabled: true,
      beFloorBufferPercent: 0.10,
      slippageBufferPercent: 0.10,
      aiCloseGuardEnabled: true,
      aiCloseGuardMinTPFraction: 0.15,
      // Pro Mode
      proMode: true,
      proModeRollout: 'on',
      proModePendingPreference: 'strong',
      scalpGateEnabled: false,
    },
  },
  {
    id: 'pro_trader_brain_10x',
    name: 'Pro Trader Brain — 10×',
    shortName: 'Pro 10×',
    description: 'Highly selective entries at 10×. Thesis memory, strict momentum, AI trailing.',
    icon: '🧠',
    intent: 'pro_selective',
    tags: ['pro', 'selective', 'thesis', '10x', 'leverage', 'strict'],
    isBuiltIn: true,
    params: {
      scanInterval: '5m',
      manageInterval: '1m',
      confidenceThreshold: '88',
      confirmationMode: false,
      strictEntryEnabled: true,
      enableVolumeControl: true,
      volumeStrictness: 'high',
      enableCorrelation: true,
      sellFilterStrict: true,
      tradingMode: 'futures',
      leverage: 10,
      leverageEntryMode: 'strict',
      maxConcurrentPositions: 2,
      allocationMode: 'equal_weight',
      reservePercent: 20,
      maxPerTrade: 2,
      maxPerSymbol: 2,
      positionSizePercent: '2',
      positionSizeUsd: '100',
      stopLossPercent: '0.6',
      takeProfitPercent: '1.2',
      exitModePreset: 'custom',
      useTrailingStop: 'Yes',
      trailingStopPercent: '0.30',
      trailingStopAiMode: true,
      profitTrailDistanceNet: 0.30,
      cooldownMinutes: '10',
      cooldownPerSymbol: 'Yes',
      // Rescue (1 add at 10×)
      rescueScaleInEnabled: true,
      maxRescueAdds: '1',
      rescueLevels: '1',
      rescueReservePercent: 5,
      rescueTriggerThresholdPct: -3.0,
      rescuePremiumPct: 10,
      rescueCooldownSec: 45,
      hardLossWallPct: -6.0,
      tslBehavior: 'DISABLE_TSL_UNTIL_RESCUES_SPENT',
      maxSignalsPerRun: '3',
      // Smart Exit
      intentAutoDetectEnabled: true,
      adaptiveProfitTrailEnabled: true,
      adaptiveProfitTrailATRMultiplier: 0.4,
      trailingStopFloorEnabled: true,
      multiTargetTPEnabled: true,
      multiTargetTightenAfterR1: 0.7,
      // Zero-Lock
      zeroLockMeaningfulEnabled: true,
      zeroLockMeaningfulRule: 'sustained',
      zeroLockSustainedTicks: 3,
      zeroLockTrailingEnabled: true,
      beFloorBufferPercent: 0.05,
      slippageBufferPercent: 0.08,
      aiCloseGuardEnabled: true,
      aiCloseGuardMinTPFraction: 0.15,
      // Pro Mode
      proMode: true,
      proModeRollout: 'on',
      proModePendingPreference: 'strong',
      scalpGateEnabled: false,
    },
  },
  {
    id: 'pro_trader_brain_20x',
    name: 'Pro Trader Brain — 20×',
    shortName: 'Pro 20×',
    description: 'Ultra-selective at 20×. Maximum discipline, pending-preferred, AI trailing.',
    icon: '🧠',
    intent: 'pro_selective',
    tags: ['pro', 'selective', 'thesis', '20x', 'leverage', 'strict'],
    isBuiltIn: true,
    params: {
      scanInterval: '5m',
      manageInterval: '1m',
      confidenceThreshold: '93',
      confirmationMode: false,
      strictEntryEnabled: true,
      enableVolumeControl: true,
      volumeStrictness: 'high',
      enableCorrelation: true,
      sidePolicy: 'buy_only',
      sellFilterStrict: true,
      tradingMode: 'futures',
      leverage: 20,
      leverageEntryMode: 'strict',
      maxConcurrentPositions: 1,
      allocationMode: 'equal_weight',
      reservePercent: 25,
      maxPerTrade: 1.5,
      maxPerSymbol: 1,
      positionSizePercent: '1.5',
      positionSizeUsd: '80',
      stopLossPercent: '0.35',
      takeProfitPercent: '0.8',
      exitModePreset: 'custom',
      useTrailingStop: 'Yes',
      trailingStopPercent: '0.15',
      trailingStopAiMode: true,
      profitTrailDistanceNet: 0.15,
      cooldownMinutes: '15',
      cooldownPerSymbol: 'Yes',
      // Rescue DISABLED at 20×
      rescueScaleInEnabled: false,
      maxRescueAdds: '0',
      rescueLevels: '0',
      rescueReservePercent: 0,
      maxSignalsPerRun: '2',
      // Smart Exit
      intentAutoDetectEnabled: true,
      adaptiveProfitTrailEnabled: true,
      adaptiveProfitTrailATRMultiplier: 0.3,
      trailingStopFloorEnabled: true,
      multiTargetTPEnabled: true,
      multiTargetTightenAfterR1: 0.65,
      // Zero-Lock (aggressive at 20×)
      zeroLockMeaningfulEnabled: true,
      zeroLockMeaningfulRule: 'sustained',
      zeroLockSustainedTicks: 2,
      zeroLockTrailingEnabled: true,
      beFloorBufferPercent: 0.03,
      slippageBufferPercent: 0.06,
      aiCloseGuardEnabled: true,
      aiCloseGuardMinTPFraction: 0.15,
      // Pro Mode
      proMode: true,
      proModeRollout: 'on',
      proModePendingPreference: 'strong',
      scalpGateEnabled: false,
    },
  },
];

// ---------------------------------------------------------------------------
// BUILT-IN SETTING ACTIONS
// ---------------------------------------------------------------------------

export const SETTING_ACTIONS = [
  // --- Profit / Risk Controls ---
  {
    id: 'daily_profit_cap',
    name: 'Daily Profit Cap + Auto Stop',
    description: 'Pause new entries after reaching daily profit target',
    category: 'profit_risk',
    icon: '🎯',
    actionType: 'operational',
    operation: {
      type: 'SET_DAILY_PROFIT_CAP',
      payload: { percent: 3, actionOnHit: 'pause' },
    },
    tags: ['risk', 'discipline'],
  },
  {
    id: 'daily_loss_cap',
    name: 'Daily Loss Cap + Auto Stop',
    description: 'Pause new entries after reaching daily loss limit',
    category: 'profit_risk',
    icon: '🛑',
    actionType: 'operational',
    operation: {
      type: 'SET_DAILY_LOSS_CAP',
      payload: { percent: 2, actionOnHit: 'pause' },
    },
    tags: ['risk', 'safety'],
  },
  {
    id: 'reduce_risk_safe_mode',
    name: 'Reduce Risk (Safe Mode)',
    description: 'Lower position size, tighten SL, raise confidence threshold',
    category: 'profit_risk',
    icon: '🛡️',
    actionType: 'param_patch',
    patch: {
      maxPerTrade: 1.5,
      maxPerSymbol: 3,
      stopLossPercent: '1',
      confidenceThreshold: '85',
    },
    tags: ['risk', 'safety', 'conservative'],
  },
  {
    id: 'aggressive_profit_lock',
    name: 'Aggressive Profit Lock',
    description: 'Earlier trailing activation, tighter distance, stronger benefit lock',
    category: 'profit_risk',
    icon: '🔒',
    actionType: 'param_patch',
    patch: {
      trailingStopPercent: '0.4',
      trailingBenefitActivation: 0.5,
      trailingBenefitLockPercent: 75,
    },
    tags: ['exit', 'profit', 'scalp'],
  },

  // --- Trade Frequency Controls ---
  {
    id: 'cooldown_booster',
    name: 'Cooldown Booster',
    description: 'Increase per-symbol cooldown to reduce overtrading',
    category: 'frequency',
    icon: '⏳',
    actionType: 'param_patch',
    patch: {
      cooldownMinutes: '10',
    },
    tags: ['frequency', 'chop'],
  },
  {
    id: 'max_concurrent_limit',
    name: 'Max Concurrent Positions Limit',
    description: 'Limit total open positions to reduce exposure',
    category: 'frequency',
    icon: '📊',
    actionType: 'operational',
    operation: {
      type: 'SET_MAX_CONCURRENT_POSITIONS',
      payload: { maxTotal: 3, maxPerSymbol: 1 },
    },
    tags: ['frequency', 'risk'],
  },

  // --- Directional Policy ---
  {
    id: 'buy_only',
    name: 'Buy-Only Mode',
    description: 'Only allow BUY entries (block all sells)',
    category: 'direction',
    icon: '📗',
    actionType: 'operational',
    operation: {
      type: 'SET_SIDE_POLICY',
      payload: { allowed: 'buy_only' },
    },
    tags: ['direction', 'bias'],
  },
  {
    id: 'sell_only',
    name: 'Sell-Only Mode',
    description: 'Only allow SELL entries (block all buys)',
    category: 'direction',
    icon: '📕',
    actionType: 'operational',
    operation: {
      type: 'SET_SIDE_POLICY',
      payload: { allowed: 'sell_only' },
    },
    tags: ['direction', 'bias'],
  },
  {
    id: 'sell_filter_tightening',
    name: 'Sell Filter Tightening',
    description: 'Require stronger confirmation for sells than buys',
    category: 'direction',
    icon: '🔍',
    actionType: 'param_patch',
    patch: {
      sellFilterStrict: true,
    },
    tags: ['direction', 'sell', 'safety'],
  },

  // --- Market Regime Protection ---
  {
    id: 'anti_chop_mode',
    name: 'Anti-Chop Mode',
    description: 'Increase strictness, reduce entries during low volatility / chop',
    category: 'regime',
    icon: '🌊',
    actionType: 'param_patch',
    patch: {
      confidenceThreshold: '85',
      volumeStrictness: 'high',
      strictEntryEnabled: true,
      cooldownMinutes: '12',
    },
    tags: ['regime', 'chop', 'safety'],
  },

  // --- Execution / Reliability ---
  {
    id: 'low_rate_limit',
    name: 'Low Rate Limit Mode',
    description: 'Reduce scan frequency to avoid API rate limits',
    category: 'execution',
    icon: '🐢',
    actionType: 'operational',
    operation: {
      type: 'SET_RATE_LIMIT_MODE',
      payload: { scanInterval: '15m', maxSignalsPerRun: 2 },
    },
    tags: ['execution', 'reliability'],
  },

  // --- Rescue Controls ---
  {
    id: 'disable_rescue',
    name: 'Disable Rescue',
    description: 'Turn off rescue scale-in completely',
    category: 'rescue',
    icon: '🚫',
    actionType: 'param_patch',
    patch: {
      rescueScaleInEnabled: false,
      rescueReservePercent: 0,
    },
    tags: ['rescue', 'scalp'],
  },
  {
    id: 'rescue_conservative',
    name: 'Rescue Conservative',
    description: 'Fewer adds, higher min time, smaller add size',
    category: 'rescue',
    icon: '🩹',
    actionType: 'param_patch',
    patch: {
      rescueScaleInEnabled: true,
      rescueReservePercent: 5,
    },
    tags: ['rescue', 'conservative'],
  },
];

// ---------------------------------------------------------------------------
// ACTION CATEGORIES (for UI grouping)
// ---------------------------------------------------------------------------

export const ACTION_CATEGORIES = [
  { id: 'profit_risk', name: 'Profit / Risk Controls', icon: '💰' },
  { id: 'frequency', name: 'Trade Frequency', icon: '⏱️' },
  { id: 'direction', name: 'Directional Policy', icon: '↕️' },
  { id: 'regime', name: 'Market Regime', icon: '🌊' },
  { id: 'execution', name: 'Execution', icon: '⚙️' },
  { id: 'rescue', name: 'Rescue Controls', icon: '🩹' },
];

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------

/** Apply a mode's params as a full snapshot, then apply action patches on top */
export function applyModeAndActions(modeParams, selectedActionIds) {
  // Start from mode's full param snapshot
  const result = { ...modeParams };

  // Apply each selected param_patch action in order
  for (const actionId of selectedActionIds) {
    const action = SETTING_ACTIONS.find(a => a.id === actionId);
    if (action && action.actionType === 'param_patch' && action.patch) {
      Object.assign(result, action.patch);
    }
  }

  return result;
}

/** Get operational actions from selected action IDs */
export function getOperationalActions(selectedActionIds) {
  return selectedActionIds
    .map(id => SETTING_ACTIONS.find(a => a.id === id))
    .filter(a => a && a.actionType === 'operational');
}

/** Build the SCHEDULE_APPLY_CONFIGURATION order payload */
export function buildConfigurationOrder({
  strategyId,
  scheduleId,
  mode,
  selectedActionIds,
  appliedParams,
  previousParams,
}) {
  // Build diff
  const diff = {};
  if (previousParams) {
    for (const key of Object.keys(appliedParams)) {
      if (JSON.stringify(appliedParams[key]) !== JSON.stringify(previousParams[key])) {
        diff[key] = { from: previousParams[key], to: appliedParams[key] };
      }
    }
  }

  const actions = selectedActionIds.map(id => {
    const action = SETTING_ACTIONS.find(a => a.id === id);
    if (!action) return null;
    return {
      actionId: action.id,
      actionName: action.name,
      actionType: action.actionType,
      isBuiltIn: true,
      patch: action.actionType === 'param_patch' ? action.patch : undefined,
      operation: action.actionType === 'operational' ? action.operation : undefined,
    };
  }).filter(Boolean);

  return {
    orderId: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    type: 'SCHEDULE_APPLY_CONFIGURATION',
    createdAt: new Date().toISOString(),
    strategyId,
    scheduleId,
    selection: {
      mode: {
        modeId: mode?.id || null,
        modeName: mode?.name || 'Custom',
        isBuiltIn: mode?.isBuiltIn || false,
      },
      actions,
    },
    appliedParams,
    diff: Object.keys(diff).length > 0 ? diff : undefined,
    intent: mode?.intent || 'custom',
  };
}
